import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../api';
import { Plus, Search, Edit2, Trash2, X, Luggage, AlertTriangle, Printer, ArrowLeftRight } from 'lucide-react';

const LIMITE_KG = 30;
const TAXA_POR_KG = 5;

const TAMANHOS_ETIQUETA = [
  { value: 'a4',      label: 'A4',      desc: 'Folha inteira, ideal para impressoras comuns' },
  { value: 'a5',      label: 'A5',      desc: 'Meia folha, ótimo para encomendas médias' },
  { value: 'termica', label: 'Térmica', desc: '100×150mm, etiqueta autocolante pequena' },
];

const STATUS_PAG = [
  { value: 'pendente', label: 'Pendente', cls: 'bg-red-100 text-red-700' },
  { value: 'pago',     label: 'Pago',     cls: 'bg-emerald-100 text-emerald-700' },
  { value: 'isento',   label: 'Isento',   cls: 'bg-slate-100 text-slate-600' },
];
const MOMENTOS = [
  { value: 'reserva',  label: 'Reserva' },
  { value: 'check-in', label: 'Check-in' },
];

const getPagCls   = (s) => STATUS_PAG.find(o => o.value === s)?.cls   || 'bg-slate-100 text-slate-600';
const getPagLabel = (s) => STATUS_PAG.find(o => o.value === s)?.label || s;

const calcExcesso = (peso) => {
  const p = parseFloat(peso) || 0;
  const excesso = Math.max(0, p - LIMITE_KG);
  return { excesso: parseFloat(excesso.toFixed(2)), taxa: parseFloat((excesso * TAXA_POR_KG).toFixed(2)) };
};

const emptyForm = {
  reserva_id: '', num_malas: 1, peso_total_kg: '', status_pagamento: 'pendente',
  momento_registro: 'reserva', observacoes: '', codigo_bagagem: '',
};

export default function Bagagens() {
  const { hasRole } = useAuth();
  const [searchParams] = useSearchParams();
  const canWrite = !hasRole('financeiro');

  const [bagagens, setBagagens]                   = useState([]);
  const [reservas, setReservas]                   = useState([]);
  const [viagens, setViagens]                     = useState([]);
  const [search, setSearch]                       = useState('');
  const [statusFilter, setStatusFilter]           = useState('');
  const [loading, setLoading]                     = useState(false);
  const [modalOpen, setModalOpen]                 = useState(false);
  const [loadingModal, setLoadingModal]           = useState(false);
  const [editingId, setEditingId]                 = useState(null);
  const [deleteId, setDeleteId]                   = useState(null);
  const [form, setForm]                           = useState(emptyForm);
  const [saving, setSaving]                       = useState(false);
  const [reservaSearch, setReservaSearch]         = useState('');
  const [selectedReserva, setSelectedReserva]     = useState(null);
  const [viagemFormFilter, setViagemFormFilter]   = useState('');
  const [showReservaDropdown, setShowReservaDropdown] = useState(false);
  const reservaSearchRef = useRef(null);
  const dropdownRef      = useRef(null);
  const [etiquetaModal, setEtiquetaModal]         = useState(null);
  const [tamanhoEtiqueta, setTamanhoEtiqueta]     = useState('a4');
  const [transferBag, setTransferBag]             = useState(null);
  const [transferViagemId, setTransferViagemId]   = useState('');
  const [savingTransfer, setSavingTransfer]       = useState(false);

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const { excesso, taxa } = calcExcesso(form.peso_total_kg);

  const fetchBagagens = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/bagagens', {
        params: { status_pagamento: statusFilter || undefined },
      });
      setBagagens(data);
    } catch { toast.error('Erro ao carregar bagagens'); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchBagagens(); }, [statusFilter]);

  useEffect(() => {
    const handler = (e) => {
      if (
        reservaSearchRef.current && !reservaSearchRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) setShowReservaDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadReservasViagens = async () => {
    const [resData, viagData] = await Promise.all([
      api.get('/reservas'),
      api.get('/viagens'),
    ]);
    setReservas(resData.data.filter(r => r.status !== 'cancelada'));
    setViagens(viagData.data);
  };

  // Abre modal direto da aba Reservas via ?reserva_id=...
  useEffect(() => {
    const reservaId = searchParams.get('reserva_id');
    if (!reservaId) return;
    (async () => {
      try {
        await loadReservasViagens();
        const { data: allRes } = await api.get('/reservas');
        const r = allRes.find(x => x.id === reservaId);
        if (r) {
          setSelectedReserva(r);
          setForm(prev => ({ ...prev, reserva_id: r.id }));
          setReservaSearch(r.cliente?.nome || '');
          setModalOpen(true);
        }
      } catch (err) {
        console.error('[Bagagens] URL param error:', err);
      }
    })();
  }, []); // eslint-disable-line

  const openCreate = async () => {
    setEditingId(null);
    setForm(emptyForm);
    setSelectedReserva(null);
    setReservaSearch('');
    setViagemFormFilter('');
    setLoadingModal(true);
    try { await loadReservasViagens(); }
    catch { toast.error('Erro ao carregar dados'); }
    finally { setLoadingModal(false); }
    setModalOpen(true);
  };

  const openEdit = async (b) => {
    setEditingId(b.id);
    setForm({
      reserva_id: b.reserva_id || '',
      num_malas: b.num_malas,
      peso_total_kg: b.peso_total_kg,
      status_pagamento: b.status_pagamento,
      momento_registro: b.momento_registro || 'reserva',
      observacoes: b.observacoes || '',
      codigo_bagagem: b.codigo_bagagem || '',
    });
    setViagemFormFilter(b.viagem_id || '');
    setSelectedReserva({ id: b.reserva_id, cliente: { nome: b.passageiro_nome }, assento: b.assento, viagem: b.viagem });
    setReservaSearch(b.passageiro_nome || '');
    setLoadingModal(true);
    try { await loadReservasViagens(); }
    catch { }
    finally { setLoadingModal(false); }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false); setEditingId(null); setForm(emptyForm);
    setSelectedReserva(null); setReservaSearch(''); setViagemFormFilter('');
  };

  const filteredReservas = reservas.filter(r => {
    const nome   = r.cliente?.nome  || '';
    const titulo = r.viagem?.titulo || '';
    const q = reservaSearch.toLowerCase();
    return (
      (nome.toLowerCase().includes(q) || titulo.toLowerCase().includes(q)) &&
      (!viagemFormFilter || r.viagem_id === viagemFormFilter)
    );
  });

  const selectReserva = (r) => {
    setSelectedReserva(r);
    setForm(prev => ({ ...prev, reserva_id: r.id }));
    setReservaSearch(r.cliente?.nome || '');
    setShowReservaDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.reserva_id) { toast.error('Selecione uma reserva'); return; }
    if (!form.peso_total_kg || parseFloat(form.peso_total_kg) <= 0) { toast.error('Informe o peso total'); return; }
    setSaving(true);
    try {
      const payload = { ...form, peso_total_kg: parseFloat(form.peso_total_kg), num_malas: parseInt(form.num_malas) };
      if (editingId) {
        await api.put(`/bagagens/${editingId}`, {
          num_malas: payload.num_malas, peso_total_kg: payload.peso_total_kg,
          status_pagamento: payload.status_pagamento, momento_registro: payload.momento_registro,
          observacoes: payload.observacoes, codigo_bagagem: payload.codigo_bagagem || null,
        });
        toast.success('Bagagem atualizada!');
      } else {
        await api.post('/bagagens', payload);
        toast.success('Bagagem registrada com sucesso!');
      }
      closeModal();
      fetchBagagens();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar bagagem');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/bagagens/${deleteId}`);
      toast.success('Bagagem excluída!');
      setDeleteId(null);
      fetchBagagens();
    } catch { toast.error('Erro ao excluir'); }
  };

  const handleGerarEtiqueta = async () => {
    if (!etiquetaModal) return;
    try {
      const res = await api.get(`/bagagens/${etiquetaModal.id}/etiqueta/pdf`, {
        params: { tamanho: tamanhoEtiqueta },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      toast.success('Etiqueta gerada! Imprima pela janela que abriu.');
      setEtiquetaModal(null);
    } catch { toast.error('Erro ao gerar etiqueta'); }
  };

  const handleTransfer = async () => {
    if (!transferViagemId) { toast.error('Selecione a viagem de destino'); return; }
    setSavingTransfer(true);
    try {
      await api.post(`/bagagens/${transferBag.id}/transferir`, { nova_viagem_id: transferViagemId });
      toast.success('Bagagem transferida com sucesso!');
      setTransferBag(null); setTransferViagemId('');
      fetchBagagens();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao transferir');
    } finally { setSavingTransfer(false); }
  };

  const temExcessoPendente = (b) => b.taxa_excesso > 0 && b.status_pagamento === 'pendente';

  const bagagensFiltradas = bagagens.filter(b =>
    !search ||
    (b.passageiro_nome || '').toLowerCase().includes(search.toLowerCase()) ||
    (b.codigo_bagagem  || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="bagagens-page">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Bagagem</h1>
          <p className="text-slate-500 text-sm mt-0.5">Controle de bagagens e excesso de peso dos passageiros</p>
        </div>
        {canWrite && (
          <button onClick={openCreate} data-testid="btn-nova-bagagem"
            className="flex items-center gap-2 text-white font-semibold px-4 py-2.5 rounded-xl text-sm transition-colors shadow-sm"
            style={{ backgroundColor: 'var(--brand-secondary)' }}>
            <Plus className="w-4 h-4" /> Registrar Bagagem
          </button>
        )}
      </div>

      {/* ── Filtros ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchBagagens()}
              placeholder="Buscar por passageiro..." data-testid="bagagem-search"
              className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[var(--brand-primary)]" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="bagagem-status-filter"
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none bg-white text-slate-700 min-w-[160px]">
            <option value="">Todos os status</option>
            {STATUS_PAG.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {/* ── Tabela ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-20 text-slate-400">
            <div className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full mr-3"
              style={{ borderColor: 'var(--brand-primary)', borderTopColor: 'transparent' }} />
            Carregando...
          </div>
        ) : bagagensFiltradas.length === 0 ? (
          <div className="text-center py-20">
            <Luggage className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">Nenhuma bagagem registrada</p>
            <p className="text-slate-400 text-sm mt-1">Registre a bagagem de um passageiro para começar</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Passageiro</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Viagem</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Assento</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Volumes</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Peso Total</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Excesso</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Taxa</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Pagamento</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden xl:table-cell">Registro</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Código</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {bagagensFiltradas.map((b) => (
                  <tr key={b.id}
                    className={`hover:bg-slate-50/60 transition-colors ${temExcessoPendente(b) ? 'bg-red-50/30' : ''}`}
                    data-testid={`bagagem-row-${b.id}`}>
                    <td className="px-5 py-3.5">
                      <p className="font-semibold text-slate-800">{b.passageiro_nome || '—'}</p>
                      {temExcessoPendente(b) && (
                        <span className="flex items-center gap-1 text-xs text-red-600 font-medium mt-0.5">
                          <AlertTriangle className="w-3 h-3" /> Excesso pendente
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 hidden md:table-cell">
                      <p className="text-xs font-medium text-slate-700">{b.viagem?.titulo || '—'}</p>
                      <p className="text-xs text-slate-400">
                        {b.viagem?.data ? new Date(b.viagem.data + 'T00:00:00').toLocaleDateString('pt-BR') : ''}
                      </p>
                    </td>
                    <td className="px-5 py-3.5 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-orange-100 text-orange-700 font-bold text-sm">
                        {b.assento}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-center hidden sm:table-cell">
                      <span className="text-sm font-medium text-slate-700">{b.num_malas}</span>
                      <span className="text-xs text-slate-400 ml-1">{b.num_malas === 1 ? 'volume' : 'volumes'}</span>
                    </td>
                    <td className="px-5 py-3.5 text-center hidden lg:table-cell">
                      <span className="text-sm font-medium text-slate-700">{b.peso_total_kg} kg</span>
                    </td>
                    <td className="px-5 py-3.5 text-center hidden lg:table-cell">
                      {b.excesso_kg > 0
                        ? <span className="text-sm font-semibold text-red-600">+{b.excesso_kg} kg</span>
                        : <span className="text-xs text-slate-400">—</span>}
                    </td>
                    <td className="px-5 py-3.5 text-right hidden sm:table-cell">
                      {b.taxa_excesso > 0
                        ? <span className={`font-bold text-sm ${b.status_pagamento === 'pago' ? 'text-emerald-600' : 'text-red-600'}`}>
                            R$ {b.taxa_excesso.toFixed(2)}
                          </span>
                        : <span className="text-xs text-slate-400">Sem taxa</span>}
                    </td>
                    <td className="px-5 py-3.5 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${getPagCls(b.status_pagamento)}`}>
                        {getPagLabel(b.status_pagamento)}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-center hidden xl:table-cell">
                      <span className="text-xs text-slate-500 capitalize">
                        {b.momento_registro === 'check-in' ? 'Check-In' : 'Reserva'}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-center hidden md:table-cell">
                      <span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-mono font-semibold tracking-wider">
                        {b.codigo_bagagem || '—'}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => { setEtiquetaModal(b); setTamanhoEtiqueta('a4'); }}
                          title="Imprimir etiqueta" data-testid={`btn-etiqueta-bagagem-${b.id}`}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors">
                          <Printer className="w-4 h-4" />
                        </button>
                        {canWrite && (
                          <>
                            <button onClick={() => openEdit(b)} title="Editar"
                              data-testid={`btn-edit-bagagem-${b.id}`}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => { setTransferBag(b); setTransferViagemId(''); }}
                              title="Transferir para outra viagem" data-testid={`btn-transfer-bagagem-${b.id}`}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-purple-600 hover:bg-purple-50 transition-colors">
                              <ArrowLeftRight className="w-4 h-4" />
                            </button>
                            <button onClick={() => setDeleteId(b.id)} title="Excluir"
                              data-testid={`btn-delete-bagagem-${b.id}`}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal Registrar / Editar ── */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="bagagem-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="font-bold text-slate-800 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {editingId ? 'Editar Bagagem' : 'Registrar Bagagem'}
              </h2>
              <button onClick={closeModal} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>

            {loadingModal ? (
              <div className="p-10 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
                <div className="animate-spin w-5 h-5 border-2 border-t-transparent rounded-full"
                  style={{ borderColor: 'var(--brand-primary)', borderTopColor: 'transparent' }} />
                Carregando dados...
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-6 space-y-4">

                {/* Filtro de viagem (só no cadastro) */}
                {!editingId && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Viagem (para filtrar passageiros)</label>
                    <select value={viagemFormFilter}
                      onChange={(e) => { setViagemFormFilter(e.target.value); setSelectedReserva(null); setReservaSearch(''); f('reserva_id', ''); }}
                      data-testid="bagagem-viagem-filter"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none bg-white focus:border-[var(--brand-primary)]">
                      <option value="">Todas as viagens</option>
                      {viagens.map(v => (
                        <option key={v.id} value={v.id}>
                          {v.titulo} — {new Date(v.data + 'T00:00:00').toLocaleDateString('pt-BR')}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Busca de passageiro / reserva */}
                <div className="relative">
                  <label className="block text-sm font-semibold text-slate-700 mb-1">
                    Passageiro / Reserva *
                    {!selectedReserva && (
                      <span className="ml-1 text-xs font-normal text-slate-400">(pode preencher os outros campos primeiro)</span>
                    )}
                  </label>
                  <div className="relative" ref={reservaSearchRef}>
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      value={reservaSearch}
                      onChange={(e) => {
                        setReservaSearch(e.target.value);
                        setShowReservaDropdown(true);
                        if (!e.target.value) { setSelectedReserva(null); f('reserva_id', ''); }
                      }}
                      onFocus={() => { if (reservaSearch.trim().length > 0) setShowReservaDropdown(true); }}
                      onKeyDown={(e) => { if (e.key === 'Escape') setShowReservaDropdown(false); }}
                      placeholder="Digite o nome do passageiro..."
                      data-testid="bagagem-reserva-search"
                      readOnly={!!editingId}
                      className={`w-full pl-9 pr-10 py-2.5 border rounded-lg text-sm focus:outline-none transition-colors ${
                        editingId
                          ? 'bg-slate-50 border-slate-200'
                          : selectedReserva
                          ? 'border-emerald-400 bg-emerald-50/40'
                          : 'border-slate-300 focus:border-[var(--brand-primary)]'
                      }`}
                    />
                    {selectedReserva && !editingId && (
                      <button type="button"
                        onClick={() => { setSelectedReserva(null); f('reserva_id', ''); setReservaSearch(''); }}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500 transition-colors p-0.5 rounded">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  {showReservaDropdown && !editingId && reservaSearch.trim().length > 0 && (
                    <div ref={dropdownRef}
                      className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                      {filteredReservas.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-slate-400 text-center">Nenhum passageiro encontrado</div>
                      ) : filteredReservas.slice(0, 8).map(r => (
                        <button key={r.id} type="button"
                          onMouseDown={(e) => { e.preventDefault(); selectReserva(r); }}
                          className="w-full text-left px-4 py-2.5 hover:bg-slate-50 border-b border-slate-100 last:border-0 transition-colors">
                          <p className="font-medium text-slate-800 text-sm">{r.cliente?.nome}</p>
                          <p className="text-xs text-slate-500">
                            {r.viagem?.titulo} — Assento #{r.assento}
                            <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold ${r.status === 'confirmada' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                              {r.status}
                            </span>
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Card da reserva selecionada */}
                {selectedReserva && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-orange-500 flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">
                      {selectedReserva.assento}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{selectedReserva.cliente?.nome}</p>
                      <p className="text-xs text-slate-500">{selectedReserva.viagem?.titulo}</p>
                    </div>
                  </div>
                )}

                {/* Volumes */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Número de Volumes *</label>
                  <input type="number" min="1" value={form.num_malas}
                    onChange={(e) => f('num_malas', parseInt(e.target.value) || 1)}
                    data-testid="bagagem-malas-input" required
                    placeholder="Ex: 3 (malas, caixas, sacos, isopor...)"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[var(--brand-primary)]" />
                  <p className="text-xs text-slate-400 mt-1">Total de volumes: malas, caixas, sacos, isopor, etc.</p>
                </div>

                {/* Peso */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Peso Total (kg) *</label>
                  <input type="number" step="0.1" min="0.1" value={form.peso_total_kg}
                    onChange={(e) => f('peso_total_kg', e.target.value)} required
                    placeholder="Ex: 32.5" data-testid="bagagem-peso-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[var(--brand-primary)]" />
                </div>

                {/* Cálculo automático */}
                {parseFloat(form.peso_total_kg) > 0 && (
                  <div className={`rounded-xl p-4 border ${excesso > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}
                    data-testid="bagagem-calculo">
                    <p className="text-xs font-bold uppercase tracking-widest mb-2 text-slate-500">Cálculo automático</p>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <p className="text-xs text-slate-500">Limite incluso</p>
                        <p className="font-bold text-slate-800">{LIMITE_KG} kg</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Excesso</p>
                        <p className={`font-bold ${excesso > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {excesso > 0 ? `+${excesso} kg` : '0 kg'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Taxa a cobrar</p>
                        <p className={`font-bold ${taxa > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {taxa > 0 ? `R$ ${taxa.toFixed(2)}` : 'Sem taxa'}
                        </p>
                      </div>
                    </div>
                    {excesso === 0 && (
                      <p className="text-xs text-emerald-600 text-center mt-2 font-medium">
                        Dentro do limite! Sem cobrança de excesso.
                      </p>
                    )}
                    {excesso > 0 && (
                      <p className="text-xs text-red-600 text-center mt-2">
                        {excesso} kg × R$ {TAXA_POR_KG},00/kg = <strong>R$ {taxa.toFixed(2)}</strong>
                      </p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Status do Pagamento</label>
                    <select value={form.status_pagamento} onChange={(e) => f('status_pagamento', e.target.value)}
                      data-testid="bagagem-status-pagamento"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none bg-white focus:border-[var(--brand-primary)]">
                      {STATUS_PAG.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Momento do Registro</label>
                    <select value={form.momento_registro} onChange={(e) => f('momento_registro', e.target.value)}
                      data-testid="bagagem-momento-registro"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none bg-white focus:border-[var(--brand-primary)]">
                      {MOMENTOS.map(m => <option key={m.value} value={m.value}>Registrado em: {m.label}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Observações (opcional)</label>
                  <textarea value={form.observacoes} onChange={(e) => f('observacoes', e.target.value)}
                    rows={2} data-testid="bagagem-obs-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none resize-none focus:border-[var(--brand-primary)]" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">
                    Código da Bagagem (opcional)
                    <span className="ml-2 text-xs font-normal text-slate-400">Se vazio, será gerado automaticamente</span>
                  </label>
                  <input type="text" value={form.codigo_bagagem} onChange={(e) => f('codigo_bagagem', e.target.value)}
                    placeholder="Ex: 13955" maxLength={20} data-testid="bagagem-codigo-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[var(--brand-primary)]" />
                </div>

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={closeModal}
                    className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">
                    Cancelar
                  </button>
                  <button type="submit" disabled={saving} data-testid="btn-salvar-bagagem"
                    className="flex-1 text-white rounded-lg text-sm font-semibold py-2.5 disabled:opacity-60"
                    style={{ backgroundColor: 'var(--brand-primary)' }}>
                    {saving ? 'Salvando...' : (editingId ? 'Atualizar' : 'Registrar Bagagem')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── Modal Transferir Bagagem ── */}
      {transferBag && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-bold text-slate-800 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>Transferir Bagagem</h2>
                <p className="text-sm text-slate-500 mt-0.5">{transferBag.passageiro_nome}</p>
              </div>
              <button onClick={() => { setTransferBag(null); setTransferViagemId(''); }}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Viagem atual</p>
                <p className="text-sm font-semibold text-slate-800">{transferBag.viagem?.titulo || '—'}</p>
                <p className="text-xs text-slate-400">{transferBag.viagem?.origem} → {transferBag.viagem?.destino}</p>
              </div>
              <div className="flex justify-center">
                <div className="flex items-center gap-2 text-purple-500">
                  <ArrowLeftRight className="w-5 h-5" />
                  <span className="text-sm font-medium">Transferir para</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Nova Viagem *</label>
                <select value={transferViagemId} onChange={(e) => setTransferViagemId(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400/20 focus:border-purple-400 bg-white">
                  <option value="">Selecione a viagem de destino</option>
                  {viagens.filter(v => v.id !== transferBag.viagem_id).map(v => (
                    <option key={v.id} value={v.id}>
                      {v.titulo} — {new Date(v.data + 'T00:00:00').toLocaleDateString('pt-BR')}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => { setTransferBag(null); setTransferViagemId(''); }}
                  className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">
                  Cancelar
                </button>
                <button type="button" onClick={handleTransfer} disabled={savingTransfer}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold py-2.5 disabled:opacity-60 flex items-center justify-center gap-2">
                  <ArrowLeftRight className="w-4 h-4" />
                  {savingTransfer ? 'Transferindo...' : 'Confirmar Transferência'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Etiqueta ── */}
      {etiquetaModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="etiqueta-bagagem-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Printer className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <h2 className="font-bold text-slate-800 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>Imprimir Etiqueta</h2>
                  <p className="text-slate-500 text-xs">
                    {etiquetaModal.passageiro_nome} — Assento {etiquetaModal.assento}
                    {etiquetaModal.codigo_bagagem && (
                      <span className="ml-2 font-mono font-semibold text-amber-600">#{etiquetaModal.codigo_bagagem}</span>
                    )}
                    {etiquetaModal.num_malas > 1 && (
                      <span className="ml-2 text-blue-600 font-semibold">{etiquetaModal.num_malas} volumes</span>
                    )}
                  </p>
                </div>
              </div>
              <button onClick={() => setEtiquetaModal(null)}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-600 font-medium">Selecione o tamanho da etiqueta:</p>
              {TAMANHOS_ETIQUETA.map((t) => (
                <label key={t.value}
                  className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${tamanhoEtiqueta === t.value ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input type="radio" name="tamanho-bagagem" value={t.value}
                    checked={tamanhoEtiqueta === t.value}
                    onChange={() => setTamanhoEtiqueta(t.value)}
                    className="mt-0.5 accent-amber-500" />
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{t.label}</p>
                    <p className="text-slate-500 text-xs">{t.desc}</p>
                  </div>
                </label>
              ))}
              <p className="text-xs text-slate-400 mt-1">
                Todas com borda tracejada para recorte. QR Code incluso.
                {etiquetaModal.num_malas > 1 && (
                  <span className="block mt-1 text-blue-600 font-medium">
                    Serão geradas {etiquetaModal.num_malas} etiquetas (uma por volume).
                  </span>
                )}
              </p>
            </div>
            <div className="flex gap-3 px-5 pb-5">
              <button onClick={() => setEtiquetaModal(null)}
                className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium">
                Cancelar
              </button>
              <button onClick={handleGerarEtiqueta} data-testid="btn-gerar-etiqueta-bagagem"
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-semibold py-2.5 flex items-center justify-center gap-2">
                <Printer className="w-4 h-4" /> Gerar e Abrir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirmar Exclusão ── */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="font-bold text-slate-800 text-lg mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Confirmar exclusão</h3>
            <p className="text-slate-500 text-sm mb-5">Deseja excluir este registro de bagagem?</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)}
                className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium">
                Cancelar
              </button>
              <button onClick={handleDelete} data-testid="btn-confirm-delete-bagagem"
                className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold py-2.5">
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
